import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Engagements from './pages/Engagements';
import EngagementDetail from './pages/EngagementDetail';
import Clients from './pages/Clients';
import VulnTemplates from './pages/VulnTemplates';
import ReportTemplates from './pages/ReportTemplates';
import Billing from './pages/Billing';
import UserManagement from './pages/UserManagement';
import GroupManagement from './pages/GroupManagement';
import PlaybookTemplates from './pages/PlaybookTemplates';
import Vulnerabilities from './pages/Vulnerabilities';
import Retest from './pages/Retest';
import Governance from './pages/Governance';
import ActivityLog from './pages/ActivityLog';
import SsoCallback from './pages/SsoCallback';
import SsoSettings from './pages/SsoSettings';
import ReportTemplateEditor from './pages/ReportTemplateEditor';
import IntegrationSettings from './pages/IntegrationSettings';
import ClientSoW from './pages/ClientSoW';
import AutoScan from './pages/AutoScan';
import AttackPathProxy from './pages/AttackPathProxy';
import AiKnowledgeBase from './pages/AiKnowledgeBase';
import Neo from './pages/Neo';
import NotificationSettings from './pages/NotificationSettings';
import Applications from './pages/Applications';
import Networks from './pages/Networks';
import ClientLicensing from './pages/ClientLicensing';
import ClientVendors from './pages/ClientVendors';
import Requests from './pages/Requests';
import SowTemplates from './pages/SowTemplates';
import SowTemplateEditor from './pages/SowTemplateEditor';
import VerifyCertificate from './pages/VerifyCertificate';
import Calendar from './pages/Calendar';
import Help from './pages/Help';
import AgentScanner from './pages/AgentScanner';
import AiRules from './pages/AiRules';

function ProtectedRoute({ children, requiredGroup, requireMsp, requiredGroups, requireAi }) {
  const { user, hasGroup, authLoading, isRequestorOnly } = useAuth();
  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;
  // An account confined to requests has nowhere else to be sent, so every
  // other route lands on Requests rather than bouncing off a dashboard it
  // cannot load either.
  const home = isRequestorOnly ? '/requests' : '/dashboard';
  if (isRequestorOnly && !allowedForRequestor(children)) return <Navigate to={home} />;
  if (requiredGroup && !hasGroup(requiredGroup)) return <Navigate to={home} />;
  if (requiredGroups && !requiredGroups.some(g => hasGroup(g))) return <Navigate to={home} />;
  if (requireMsp && !user.isMsp && !user.groups?.includes('Super Admins')) return <Navigate to={home} />;
  // aiEnabled is resolved by the API (group AND the client's AI Enabled flag),
  // so the route never re-derives that rule and cannot drift from it.
  if (requireAi && !user.aiEnabled) return <Navigate to={home} />;
  return <Layout>{children}</Layout>;
}

// The one page a confined requester may open. Compared by component rather
// than by path so the check cannot drift from the route table.
const allowedForRequestor = (children) => children?.type === Requests;

// Where an account belongs when it has not asked for anywhere in particular.
const landingPath = (user) => {
  const groups = user?.groups || [];
  const confined = groups.includes('Requestors') &&
    !groups.some(g => ['Super Admins', 'Engagement Admins', 'Client Admins', 'Technical Managers',
                       'MSP Billing Admins', 'Client Billing Admins', 'Report Admins',
                       'Penetration Testers', 'MSP Penetration Testers', 'Application Managers'].includes(g));
  return confined ? '/requests' : '/dashboard';
};

function AppRoutes() {
  const { user, authLoading } = useAuth();
  if (authLoading) return null;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={landingPath(user)} /> : <Login />} />
      <Route path="/sso/callback" element={<SsoCallback />} />
      <Route path="/verify/:token" element={<VerifyCertificate />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/requests" element={<ProtectedRoute requiredGroup="Requestors"><Requests /></ProtectedRoute>} />
      <Route path="/applications" element={<ProtectedRoute requiredGroup="Application Managers"><Applications /></ProtectedRoute>} />
      <Route path="/networks" element={<ProtectedRoute requiredGroup="Application Managers"><Networks /></ProtectedRoute>} />
      <Route path="/admin/licensing" element={<ProtectedRoute><ClientLicensing /></ProtectedRoute>} />
      <Route path="/admin/vendors" element={<ProtectedRoute><ClientVendors /></ProtectedRoute>} />
      <Route path="/engagements" element={<ProtectedRoute><Engagements /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute requiredGroups={['Engagement Admins', 'Super Admins']}><Calendar /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
      <Route path="/agent-scanner" element={<ProtectedRoute requireAi><AgentScanner /></ProtectedRoute>} />
      <Route path="/admin/ai-rules" element={<ProtectedRoute requiredGroups={['Super Admins']}><AiRules /></ProtectedRoute>} />
      <Route path="/engagements/:id" element={<ProtectedRoute><EngagementDetail /></ProtectedRoute>} />
      <Route path="/vulnerabilities" element={<ProtectedRoute><Vulnerabilities /></ProtectedRoute>} />
      <Route path="/retest" element={<ProtectedRoute requiredGroups={['Penetration Testers', 'MSP Penetration Testers', 'Engagement Admins']}><Retest /></ProtectedRoute>} />
      <Route path="/governance" element={<ProtectedRoute requiredGroups={['Client Admins', 'Engagement Admins']}><Governance /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute requireMsp><Clients /></ProtectedRoute>} />
      <Route path="/vuln-templates" element={<ProtectedRoute><VulnTemplates /></ProtectedRoute>} />
      <Route path="/report-templates" element={<ProtectedRoute requiredGroup="Report Admins"><ReportTemplates /></ProtectedRoute>} />
      <Route path="/report-templates/:id/edit" element={<ProtectedRoute requiredGroup="Report Admins"><ReportTemplateEditor /></ProtectedRoute>} />
      <Route path="/sow-templates" element={<ProtectedRoute requiredGroups={['MSP Billing Admins', 'Super Admins']}><SowTemplates /></ProtectedRoute>} />
      <Route path="/sow-templates/:id/edit" element={<ProtectedRoute requiredGroups={['MSP Billing Admins', 'Super Admins']}><SowTemplateEditor /></ProtectedRoute>} />
      <Route path="/auto-scan" element={<ProtectedRoute requiredGroups={['Super Admins']}><AutoScan /></ProtectedRoute>} />
      <Route path="/tools/proxy" element={<ProtectedRoute requiredGroup="AttackPath Proxy"><AttackPathProxy /></ProtectedRoute>} />
      <Route path="/playbook-templates" element={<ProtectedRoute><PlaybookTemplates /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute requiredGroups={['MSP Billing Admins', 'Client Billing Admins']}><Billing /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/admin/groups" element={<ProtectedRoute requiredGroup="Super Admins"><GroupManagement /></ProtectedRoute>} />
      <Route path="/admin/activity-log" element={<ProtectedRoute requiredGroups={['Client Admins', 'Engagement Admins']}><ActivityLog /></ProtectedRoute>} />
      <Route path="/admin/sso" element={<ProtectedRoute requiredGroup="Super Admins"><SsoSettings /></ProtectedRoute>} />
      <Route path="/admin/statements-of-work" element={<ProtectedRoute><ClientSoW /></ProtectedRoute>} />
      <Route path="/admin/ai-knowledge" element={<ProtectedRoute requiredGroups={['Super Admins', 'AI KB Admins']}><AiKnowledgeBase /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute requiredGroups={['Client Admins']}><IntegrationSettings /></ProtectedRoute>} />
      <Route path="/integrations/:clientId" element={<ProtectedRoute requiredGroups={['Client Admins']}><IntegrationSettings /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
      <Route path="/neo" element={user ? <Neo /> : <Navigate to="/login" />} />
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
